/**
 * The dirty-state machinery — digitaladmin's Global Standard, applied app-wide:
 * "UnsavedChangesModal must intercept ALL navigation away from a dirty form."
 *
 * Pages/components register their dirtiness with useDirty(); one router-level
 * blocker owns the modal, so no page reimplements the pending-action dance.
 * beforeunload covers the tab-close/refresh case the router can't see.
 * Keep = stay exactly where you were. Discard = the buffered navigation runs.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useBlocker } from 'react-router-dom';
import { Button } from '../components/ui/button';

const DirtyCtx = createContext<{
  setDirty: (id: string, dirty: boolean) => void;
  anyDirty: boolean;
  confirmDiscard: () => Promise<boolean>;
}>({
  setDirty: () => {},
  anyDirty: false,
  confirmDiscard: async () => true,
});

export function DirtyProvider({ children }: { children: ReactNode }) {
  // A SET of dirty sources, not a boolean: two dirty widgets, one saved,
  // must not silently clear the other's guard.
  const sources = useRef<Set<string>>(new Set());
  const [anyDirty, setAnyDirty] = useState(false);

  const setDirty = useCallback((id: string, dirty: boolean) => {
    const s = sources.current;
    const had = s.has(id);
    if (dirty && !had) s.add(id);
    else if (!dirty && had) s.delete(id);
    else return;
    setAnyDirty(s.size > 0);
  }, []);

  // Tab close / refresh / external nav — the browser's own dialog is all we get.
  useEffect(() => {
    if (!anyDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [anyDirty]);

  /**
   * The blocker reads the REF, not the state.
   *
   * `useBlocker(anyDirty)` captures a boolean at render, and that is one tick
   * too late for the commonest case in this app: a page whose Save or Cancel
   * both clears its dirty flag AND navigates (an editor keyed off a query
   * param, so closing it IS a navigation). Both updates batch, the router
   * processes the navigation against the blocker from the PREVIOUS render,
   * and the guard fires on an action that had nothing unsaved about it.
   *
   * Kai, 2026-09-06, on the automations editor: "When I hit save or cancel
   * this shows up, and closes it anyway. I hit keep editing and nothing
   * happens" — and the second half is the worse half. The local editor state
   * had already been torn down, so "Keep editing" cancelled the navigation
   * and left him on a URL still naming a rule with no editor open. The guard
   * was protecting work that had just been saved, then failing to give it
   * back.
   *
   * A function blocker is evaluated AT navigation time, so a `clearDirty()`
   * in the same handler is already visible. That makes "clear, then navigate"
   * work the way anyone writing it would expect.
   */
  const blocker = useBlocker(useCallback(() => sources.current.size > 0, []));

  /**
   * The same question, asked WITHOUT a navigation.
   *
   * `useBlocker` only sees the router, and some closes are not navigations —
   * a KB editor panel folding away, a rail closing. Those used to ask with
   * `window.confirm`, which meant one app asking the same question two
   * visibly different ways (Kai: *"sweep for browser discards, I prefer
   * native"* — native to the app). This renders the identical dialog and
   * resolves a promise, so a caller reads `if (!(await confirmDiscard()))
   * return;` and gets the styled one.
   */
  const [pending, setPending] = useState<((ok: boolean) => void) | null>(null);
  const confirmDiscard = useCallback(async () => {
    if (sources.current.size === 0) return true;
    return new Promise<boolean>((resolve) => setPending(() => resolve));
  }, []);
  const answer = (ok: boolean) => {
    if (ok) {
      sources.current.clear();
      setAnyDirty(false);
    }
    pending?.(ok);
    setPending(null);
  };

  return (
    <DirtyCtx.Provider value={{ setDirty, anyDirty, confirmDiscard }}>
      {children}
      {(blocker.state === 'blocked' || pending) && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div
            role="alertdialog"
            aria-label="Unsaved changes"
            className="w-full max-w-sm rounded-lg border bg-card p-4 shadow-lg"
          >
            <h2 className="text-sm font-semibold">You have unsaved changes</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Leaving now will throw them away. This is the only warning.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => (pending ? answer(false) : blocker.reset?.())}
              >
                Keep editing
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  if (pending) return answer(true);
                  sources.current.clear();
                  setAnyDirty(false);
                  blocker.proceed?.();
                }}
              >
                Discard changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </DirtyCtx.Provider>
  );
}

/**
 * Declare "this component holds unsaved work" — registration clears itself on
 * unmount so a discarded form can't leave a stuck guard behind.
 *
 * Returns `clearDirty()` for the one case the declarative flag cannot express:
 * an explicit Save or Cancel that ALSO navigates. Call it before navigating —
 * the blocker reads the same set synchronously, so the guard correctly does
 * not fire on an action the user took deliberately.
 */
export function useDirty(dirty: boolean): () => void {
  const id = useId();
  const { setDirty } = useContext(DirtyCtx);
  useEffect(() => {
    setDirty(id, dirty);
    return () => setDirty(id, false);
  }, [id, dirty, setDirty]);
  return useCallback(() => setDirty(id, false), [id, setDirty]);
}

/**
 * Ask the unsaved-changes question for a close that is NOT a navigation.
 *
 * Resolves true when there is nothing dirty, so callers need no second check.
 */
export function useConfirmDiscard(): () => Promise<boolean> {
  return useContext(DirtyCtx).confirmDiscard;
}
